import { useRef, useState } from "react";
import "./index.scss";
import React from "react";
import { PillElement } from "./PillElement";
import { iPill } from "../../../../interfaces/Pill";
import { EKeyboardKeys } from "../../../../constants/KeyboardKeys";
import { isEmail } from "../../../../interfaces/emailModel";
import parse from "../../../../service/email-parser/email-parser";
import { ContactList } from "./ContactsDropdown";
import useOutsideClick from "../../../../hooks/useOutsideClick";
import { ContactDataType, iContact, iContactData } from "../../../../interfaces/Contact";
import iEmailAddress from "../../../../interfaces/iEmailAddress";
import { AddUserContact, addUserContactsInterface, DeleteUserContact, getResults } from "./contacts.helper";
import Confirm from "../../../Generic/ConfirmDialog";
import TextBox from "../../../Generic/Form/TextBox";
import store, { RootState } from "../../../../state/store";
import { useSelector } from "react-redux";
import { iUserInformation, setUser } from "../../../../state/userSlice";
import { IconMenuItems } from "../../../Menu/icon-menu-items";
import debounce from "../../../../utilities/debounce";

interface Props {
    label: string;
    value: string;
    values?: iEmailAddress[];
    onChange: (newValue: string) => void;
    onChangeValues: (newValues: iEmailAddress[]) => void;
    setFocus?: boolean;
    isNotValid?: boolean;
    internalButton?: {
        label: string;
        onClick: () => void;
    };
    allContacts: iContactData[];
}

export function EmailDropdownInputWithPills(props: Props) {
    const [fieldValue, setFieldValue] = useState(props.value);
    const [isFocused, setIsFocused] = useState(props.setFocus ?? false);
    const [pills, setPills] = useState<iPill[]>([]);
    const [contactShown, setContactShown] = useState(false);
    const [addContactUi, setAddContactUi] = useState<addUserContactsInterface>({} as addUserContactsInterface);
    const [searchResults, setSearchResults] = useState<iContact[]>([]);
    const [showContactContext, setShowContactContext] = useState<{
        visible: boolean;
        name: string;
        email: string;
        x: number;
        y: number;
    }>({ visible: false, name: "", email: "", x: 0, y: 0 });

    const emailControllRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const userContactContextRef = useRef<HTMLDivElement>(null);
    const currentUser = useSelector((state: RootState) => state.UserInformation);

    useOutsideClick(dropdownRef, () => {
        addNewPill(fieldValue);
        setContactShown(false);
    });
    const debouncedSearchRef = useRef(
        debounce(async (value: string, allContacts: iContactData[], values: iEmailAddress[]) => {
            const results = await getResults(value, allContacts, values);
            setSearchResults(
                results.map((ct, i) => ({
                    ...ct,
                    id: `search-contact-result-element-${i}`,
                })),
            );
        }, 300),
    );
    React.useEffect(() => {
        debouncedSearchRef.current(fieldValue, props.allContacts, props.values);
    }, [fieldValue, props.values]);

    const updateSearchResults = async () => {
        let results = await getResults(fieldValue, props.allContacts, props.values);
        setSearchResults(
            results.map((ct, i) => ({
                ...ct,
                id: `search-contact-result-element-${i}`,
            })),
        );
    };

    React.useEffect(() => {
        setPills(props.values?.map((pd, i) => ({ ...pd, id: `pill-element-${i}` })) ?? []);
    }, [props.values]);

    const handleContactClick = (id: string) => {
        const clickedContact: iContact = searchResults.find(ct => {
            return ct.id === id;
        })!;
        hardAddNewPill(clickedContact.address, clickedContact.name);
    };

    const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.value.trimStart();

        // onPaste with multiple emails, capture all or none
        const emailsParsed: iEmailAddress[] = parse(newValue); // ignored groups
        const validParsedEmails: iEmailAddress[] = emailsParsed.filter(ep => isEmail(ep.address));
        if (emailsParsed.length > 1 && emailsParsed.length === validParsedEmails.length) {
            const validEmailPills: iPill[] = validParsedEmails.map((em, i) => {
                return {
                    name: em.name,
                    address: em.address.trim(),
                    id: `pill-element-${i + pills.length}`,
                };
            });

            const newPills: iPill[] = [...pills, ...validEmailPills];
            setPills(newPills);
            props.onChangeValues(newPills);
            setFieldValue("");
            props.onChange("");
            return;
        }

        // on type or there is/are pasted value/s that failed validation
        setFieldValue(newValue);
        props.onChange(newValue);
    };

    const handleUnfocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        setIsFocused(false);
    };

    const handlePillClose = (id: string) => {
        const newPills = pills.filter(p => p.id !== id);
        setPills(newPills);
        props.onChangeValues(newPills);
    };
    const closeLastPill = () => {
        const newPills: iPill[] = JSON.parse(JSON.stringify(pills));
        newPills.pop();
        setPills(newPills);
        props.onChangeValues(newPills);
    };

    const addNewPill = (address: string, name?: string) => {
        const valid = isEmail(address);
        if (valid) {
            hardAddNewPill(address, name);
        }
    };

    const hardAddNewPill = (address: string, name?: string) => {
        const newPills: iPill[] = JSON.parse(JSON.stringify(pills));
        newPills.push({ address, name, id: `pill-element-${newPills.length}` });
        setPills(newPills);
        props.onChangeValues(newPills);
        setFieldValue("");
        props.onChange("");
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const key = e.code;

        if (!fieldValue) {
            if (key === EKeyboardKeys.BACKSPACE) {
                closeLastPill();
            }
        } else {
            if (
                key === EKeyboardKeys.ENTER ||
                key === EKeyboardKeys.ENTER_NUMPAD ||
                key === EKeyboardKeys.SPACE ||
                key === EKeyboardKeys.COMMA ||
                key === EKeyboardKeys.SEMICOLON
            ) {
                addNewPill(fieldValue);
            }
        }
    };

    const onFocusMethod = () => {
        debouncedSearchRef.current(fieldValue, props.allContacts, props.values);
        setContactShown(true);
        setIsFocused(true);
    };
    const addContact = async () => {
        if (addContactUi.name.trim() == "" || addContactUi.name == undefined) {
            setAddContactUi(ps => ({ ...ps, validationErrors: "Name cannont be blank" }));
            return;
        }
        if (addContactUi.emailAddress.trim() == "" || addContactUi.emailAddress == undefined) {
            setAddContactUi(ps => ({ ...ps, validationErrors: "Email Address cannont be blank" }));
            return;
        }
        let newPills: iPill[] = [...pills];
        if (addContactUi.previousEmail != "") {
            // if it is an edit we first delete the email adress
            const rr = await DeleteUserContact({ EmailAddress: addContactUi.emailAddress.trim() });
            newPills = newPills.filter(x => x.address != addContactUi.previousEmail);
        }
        if (addContactUi.emailAddress != "") {
            newPills = newPills.filter(x => x.address != addContactUi.emailAddress);
        }
        const res = await AddUserContact({
            name: addContactUi.name.trim(),
            emailAddress: addContactUi.emailAddress.trim(),
        });
        if (!res.isOk) {
            if (res.status === 400) {
                const errors = res.validationErrors?.errors;
                if (errors) {
                    let errorMessages: string[] = [];
                    Object.keys(errors).forEach(x => {
                        errorMessages.push(errors[x][0]);
                    });
                    setAddContactUi(ps => ({ ...ps, validationErrors: errorMessages.join("<br>") }));
                    return;
                }
            }
        }
        store.dispatch(setUser({ ...currentUser, updatedAt: new Date().toString() }));
        const newEmailPill: iPill = {
            name: addContactUi.name,
            address: addContactUi.emailAddress.trim(),
            id: `pill-element-${+pills.length}`,
        };
        newPills.push(newEmailPill);
        setPills(newPills);
        props.onChangeValues(newPills);
        setAddContactUi(ps => ({ ...ps, showModal: false, emailAddress: "", name: "", previousEmail: "" }));
    };

    const showAddContactModal = () => {
        setContactShown(false);
        setAddContactUi(z => ({ ...z, showModal: true, emailAddress: fieldValue, previousEmail: "" }));
    };

    const contextMenuShow = (sName: string, sEmail: string) => (event: React.MouseEvent) => {
        event.preventDefault();
        setShowContactContext({ visible: true, name: sName, email: sEmail, x: event.clientX, y: event.clientY });
    };
    useOutsideClick(userContactContextRef, () => {
        setShowContactContext({ visible: false, name: "", email: "", x: 0, y: 0 });
    });
    return (
        <div className="d-flex">
            {props.label && (
                <button
                    disabled
                    className="g-btn-disabled pills-input-label limit-text"
                >
                    {props.label}
                </button>
            )}
            <div
                className="pills-input-with-dropdown"
                ref={dropdownRef}
            >
                <div className={`pills-input${isFocused ? " focused" : ""}${props.isNotValid ? " invalid" : ""}`}>
                    {pills.length > 0 && (
                        <div className="pills-list">
                            {pills.map((v, i) => (
                                <PillElement
                                    key={`pill-element-${i}`}
                                    {...v}
                                    onClick={handlePillClose}
                                    onContextMenu={contextMenuShow}
                                />
                            ))}
                        </div>
                    )}

                    <input
                        value={fieldValue}
                        className="pills-input-input"
                        type="text"
                        ref={emailControllRef}
                        onChange={handleInputChange}
                        onFocus={onFocusMethod}
                        onBlur={handleUnfocus}
                        onKeyDown={handleKeyPress}
                        autoComplete="off"
                        aria-autocomplete="list"
                        aria-controls="autocomplete-list"
                    />

                    {props.internalButton && (
                        <button
                            onClick={props.internalButton.onClick}
                            className="pills-input-inner-label limit-text"
                        >
                            {props.internalButton.label}
                        </button>
                    )}
                </div>

                {contactShown && (
                    <ContactList
                        onContactClick={handleContactClick}
                        onAddContactClick={showAddContactModal}
                        contacts={searchResults}
                    />
                )}
                {addContactUi.showModal && (
                    <Confirm
                        onConfirm={addContact}
                        onCancel={() => {
                            setAddContactUi(ps => ({ ...ps, showModal: false, previousEmail: "" }));
                        }}
                        title={"My Contacts"}
                    >
                        <div style={{ width: "470px" }}>
                            <TextBox
                                id={"rName"}
                                label={"Recipient Name"}
                                value={addContactUi.name}
                                onChange={(val: string) => {
                                    setAddContactUi(ps => ({ ...ps, name: val, previousEmail: "" }));
                                }}
                                maxLength={50}
                            />
                            <TextBox
                                id={"eMailAddress"}
                                label={"Email Adrress"}
                                value={addContactUi.emailAddress}
                                onChange={(val: string) => {
                                    setAddContactUi(ps => ({ ...ps, emailAddress: val, previousEmail: "" }));
                                }}
                                maxLength={50}
                            />
                            <p style={{ color: "red", fontSize: "7pt" }}>{addContactUi.validationErrors}</p>
                        </div>
                    </Confirm>
                )}
            </div>
            {showContactContext.visible && (
                <div
                    style={{
                        top: showContactContext.y + "px",
                        left: showContactContext.x + "px",
                        position: "fixed",
                        float: "left",
                        zIndex: "50",
                    }}
                    ref={userContactContextRef}
                >
                    <IconMenuItems
                        options={[
                            {
                                icon: <i></i>,
                                label: "Add to My Contacts",
                                onClick: () => {
                                    setAddContactUi(z => ({
                                        ...z,
                                        showModal: true,
                                        previousEmail: "",
                                        name: showContactContext.name,
                                        emailAddress: showContactContext.email,
                                    }));
                                    setShowContactContext({ visible: false, name: "", email: "", x: 0, y: 0 });
                                },
                                disabled: props.allContacts.find(
                                    x =>
                                        x.address == showContactContext.email &&
                                        x.contactType == ContactDataType.MyContacts,
                                )
                                    ? true
                                    : false,
                            },
                            {
                                icon: <i></i>,
                                label: "Edit",
                                onClick: () => {
                                    setAddContactUi(z => ({
                                        ...z,
                                        showModal: true,
                                        previousEmail: showContactContext.email,
                                        name: showContactContext.name,
                                        emailAddress: showContactContext.email,
                                    }));
                                    setShowContactContext({ visible: false, name: "", email: "", x: 0, y: 0 });
                                },
                                disabled: props.allContacts.find(
                                    x =>
                                        x.address == showContactContext.email &&
                                        x.contactType == ContactDataType.MyContacts,
                                )
                                    ? false
                                    : true,
                            },
                        ]}
                        menuStyle={"context-menu"}
                    ></IconMenuItems>
                </div>
            )}
        </div>
    );
}
